/**
 * How far back a trend looks: one of four presets, or a window the reader picks.
 *
 * A port of `bimgo-ai-web`'s `DashboardRangeControls` + `DashboardDateRangePicker`,
 * with one substitution forced by 2.5's stack. The reference opens a
 * `react-day-picker` calendar in a popover; this uses two native
 * `<input type="date">` fields. The dependency is the whole reason — a calendar
 * component, `date-fns` and its locale bundles for a control that produces two
 * `yyyy-MM-dd` strings, in a renderer whose CSP has to justify every byte. The
 * native input already speaks the reader's locale and offers their own picker.
 *
 * Everything the reference *decides* is kept:
 *
 *   - **The picked end day is inclusive on screen and exclusive on the wire.**
 *     api-v2's `to` is half-open, so applying adds a day. The label prints the
 *     day the reader chose, not the one the request carries.
 *   - **Both dates or nothing**, and **366 days maximum** — checked on Apply,
 *     against `rangeError`, so the rule is the same one the client enforces
 *     everywhere and lives in one place.
 *   - **The draft is not the selection.** Typing a date changes nothing until
 *     Apply; the reference is a popover with an Apply button for the same
 *     reason — a half-typed range would fire a request per keystroke.
 *   - **The future is out of reach**, bounded by *the server's* today
 *     (`asOf`), not the reader's clock: a machine a day ahead would otherwise
 *     ask for a day the server has no data for.
 *   - **A custom range is a fifth, disabled option** in the preset select, so
 *     the control still shows what is on screen rather than a preset that is
 *     not in force. Choosing a preset clears it.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  RANGES,
  formatDateRange,
  rangeError,
  type Range,
  type UsageDateRange,
} from "../../lib/usage";

export function RangeControls({
  preset,
  custom,
  asOf,
  onPreset,
  onCustom,
}: {
  preset: Range;
  custom: UsageDateRange | null;
  /** The server's own date; the picker will not offer a day past it. */
  asOf: string | null;
  onPreset: (days: Range) => void;
  onCustom: (range: UsageDateRange | null) => void;
}) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Seed the draft from what is in force each time the panel opens, so a
  // cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setFrom(custom ? custom.from : "");
    // `custom.to` is exclusive; the box shows the day the reader picked.
    setTo(custom ? inclusiveEnd(custom.to) : "");
    setError(null);
  }, [open, custom]);

  const apply = () => {
    const problem = rangeError(from || null, to || null);
    if (problem) {
      setError(problem);
      return;
    }
    onCustom({ from, to: exclusiveEnd(to) });
    setError(null);
    setOpen(false);
  };

  const reset = () => {
    setFrom("");
    setTo("");
    setError(null);
    setOpen(false);
    onCustom(null);
  };

  return (
    <div className="range-controls">
      <select
        className="settings-input select-sm"
        value={custom ? "custom" : String(preset)}
        aria-label={t("dashboard.chart.title")}
        onChange={(e) => {
          const value = e.target.value;
          // The custom entry exists to *report* the state, not to enter it —
          // the picker below is how a range is chosen.
          if (value === "custom") return;
          onPreset(Number(value) as Range);
          onCustom(null);
        }}
      >
        {RANGES.map((days) => (
          <option key={days} value={days}>
            {t(`dashboard.chart.range.${days}d`)}
          </option>
        ))}
        {custom ? (
          <option value="custom" disabled>
            {t("dashboard.analytics.customRange")}
          </option>
        ) : null}
      </select>

      <button
        type="button"
        className="settings-btn secondary btn-sm"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {custom
          ? formatDateRange(custom.from, custom.to, locale)
          : t("dashboard.analytics.customRange")}
      </button>

      {custom ? (
        <button type="button" className="settings-btn ghost btn-sm" onClick={reset}>
          {t("dashboard.analytics.reset")}
        </button>
      ) : null}

      {open ? (
        <div className="range-picker">
          <label className="range-field">
            <span className="quota-label">{t("dashboard.analytics.from")}</span>
            <input
              type="date"
              className="settings-input"
              value={from}
              max={asOf ?? undefined}
              onChange={(e) => {
                setFrom(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label className="range-field">
            <span className="quota-label">{t("dashboard.analytics.to")}</span>
            <input
              type="date"
              className="settings-input"
              value={to}
              max={asOf ?? undefined}
              onChange={(e) => {
                setTo(e.target.value);
                setError(null);
              }}
            />
          </label>
          {error ? (
            <p className="settings-msg err" role="alert">
              {t(error)}
            </p>
          ) : null}
          <div className="range-actions">
            <button type="button" className="settings-btn secondary btn-sm" onClick={() => setOpen(false)}>
              {t("limits.cancel")}
            </button>
            <button type="button" className="settings-btn btn-sm" onClick={apply}>
              {t("dashboard.analytics.apply")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The reader's last day → api-v2's exclusive `to`. */
function exclusiveEnd(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** And back, for the box. */
function inclusiveEnd(exclusive: string): string {
  const date = new Date(`${exclusive.slice(0, 10)}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}
